import React, { useState, useEffect } from 'react';
import { sinistroService } from '../../services';
import { formatCpfCnpj } from '../../utils/formatters';
import { DigisacService } from '../../services/digisacService';
import { useNotifier } from '../ui/Notifier';

interface SinistroModalProps {
	isOpen: boolean;
	onClose: () => void;
	sinistro?: any;
	onSave?: (sinistroData: any) => void;
	readOnly?: boolean;
}

const SinistroModal: React.FC<SinistroModalProps> = ({
	isOpen,
	onClose,
	sinistro,
	onSave,
	readOnly = false
}) => {
	const notifier = useNotifier();
	const [valorFranquiaDisplay, setValorFranquiaDisplay] = useState('');
	const [formData, setFormData] = useState({
		// Checkboxes flags
		aviso: false,
		oficina: false,
		abertura: false,
		documentos: false,
		vistoria_sinistro: false,
		analise: false,
		aprovacao: false,
		pagamento: false,
		concluido: false,
		recusado: false,
		
		// Campos principais
		data: '',
		tipo: '',
		valor_franquia: '',
		numero_sinistro: '',
		proposta: '',
		apolice: '',
		endosso: '',
		cpf_cnpj: '',
		nome: '',
		informacoes: '',
		motivoRecusa: '',

		// Terceiros
		terceiroNome: '',
		terceiroContato: '',
		terceiroVeiculo: '',
		terceiroSeguradora: '',
		terceiroObs: '',
	});

	useEffect(() => {
		if (sinistro) {
			const valorFranquia = sinistro.valor_franquia || '';
			setFormData({
				aviso: sinistro.aviso || false,
				oficina: sinistro.oficina || false,
				abertura: sinistro.abertura || false,
				documentos: sinistro.documentos || false,
				vistoria_sinistro: sinistro.vistoria_sinistro || false,
				analise: sinistro.analise || false,
				aprovacao: sinistro.aprovacao || false,
				pagamento: sinistro.pagamento || false,
				concluido: sinistro.concluido || false,
				recusado: sinistro.recusado || false,
				data: sinistro.data ? new Date(sinistro.data).toISOString().slice(0, 10) : '',
				tipo: sinistro.tipo || '',
				valor_franquia: valorFranquia,
				numero_sinistro: sinistro.numero_sinistro || '',
				proposta: sinistro.proposta || sinistro.numeroDocumento || '',
				apolice: sinistro.apolice || '',
				endosso: sinistro.endosso || '',
				cpf_cnpj: sinistro.cpf_cnpj || sinistro.cliente?.cpfCnpj || (sinistro.clientes && sinistro.clientes[0]?.cpfCnpj) || sinistro.documento?.cpfCnpj || '',
				nome: sinistro.nome || sinistro.cliente?.nome || (sinistro.clientes && sinistro.clientes[0]?.nome) || '',
				informacoes: sinistro.informacoes || '',
				motivoRecusa: sinistro.motivoRecusa || '',
				terceiroNome: sinistro.terceiroNome || '',
				terceiroContato: sinistro.terceiroContato || '',
				terceiroVeiculo: sinistro.terceiroVeiculo || '',
				terceiroSeguradora: sinistro.terceiroSeguradora || '',
				terceiroObs: sinistro.terceiroObs || '',
			});
			setValorFranquiaDisplay(valorFranquia ? formatCurrencyDisplay(parseFloat(valorFranquia.toString())) : '');
		} else {
			// Limpar todos os campos quando sinistro é undefined (Novo Sinistro)
			setFormData({
				oficina: false,
				abertura: false,
				documentos: false,
				vistoria_sinistro: false,
				analise: false,
				aprovacao: false,
				pagamento: false,
				concluido: false,
				data: '',
				tipo: '',
				valor_franquia: '',
				numero_sinistro: '',
				proposta: '',
				apolice: '',
				endosso: '',
				cpf_cnpj: '',
				nome: '',
				informacoes: '',
				aviso: false,
				recusado: false,
				motivoRecusa: '',
				terceiroNome: '',
				terceiroContato: '',
				terceiroVeiculo: '',
				terceiroSeguradora: '',
				terceiroObs: '',
			});
		setValorFranquiaDisplay('');
	}
}, [sinistro]);

const formatCurrencyDisplay = (value: number): string => {
	if (!value) return '';
	return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const parseCurrencyInput = (value: string): number => {
	if (!value) return 0;
	const cleanValue = value.replace(/\./g, '').replace(',', '.');
	return parseFloat(cleanValue) || 0;
};

const handleValorFranquiaChange = (value: string) => {
	const numericValue = parseCurrencyInput(value);
	setFormData(prev => ({ ...prev, valor_franquia: numericValue.toString() }));
	setValorFranquiaDisplay(formatCurrencyDisplay(numericValue));
};

const handleInputChange = (field: string, value: any) => {
	setFormData(prev => ({ ...prev, [field]: value }));
};

const handleCpfCnpjChange = (value: string) => {
  const digits = value.replace(/\D/g, '');
  setFormData(prev => ({ ...prev, cpf_cnpj: digits }));
};

const handleCheckboxChange = (field: string) => {
	setFormData(prev => ({ ...prev, [field]: !prev[field as keyof typeof prev] }));
};

const handleSave = async () => {
	try {
		const { proposta, nome, ...restFormData } = formData;
		const payload = {
			...restFormData,
			numeroDocumento: proposta,
			data: formData.data ? new Date(formData.data).toISOString() : undefined,
			valor_franquia: formData.valor_franquia ? parseFloat(formData.valor_franquia.toString()) : undefined,
			motivoRecusa: formData.recusado ? formData.motivoRecusa : undefined,
		};
		
		if (sinistro?.id) {
			await sinistroService.update(sinistro.id, payload);
		} else {
			await sinistroService.create(payload);
		}

		if (onSave) {
			onSave(payload);
		}
		onClose();
	} catch (error) {
		console.error('Erro ao salvar sinistro:', error);
		notifier.show({ message: 'Erro ao salvar sinistro', severity: 'error' });
	}
};

const handleEnviarLinkDigisac = async () => {
	const telefone = formData.cpf_cnpj ? '' : '';
	const contato = formData.nome || sinistro?.nome || '';
	try {
		await DigisacService.sendMessage({
			to: telefone || '',
			text: `Olá ${contato}, segue o link para envio dos documentos do seu sinistro nº ${formData.numero_sinistro || sinistro?.id || ''}. Por favor acesse e anexe os documentos solicitados.`,
			contactId: '',
		} as any);
		notifier.show({ message: 'Mensagem enviada via Digisac', severity: 'success' });
	} catch (error) {
		notifier.show({ message: 'Erro ao enviar mensagem Digisac', severity: 'error' });
	}
};
		
	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
				{/* Header */}
				<div className="flex items-center justify-between p-4 border-b" style={{ backgroundColor: '#7A61B2', color: 'white' }}>
					<div className="flex items-center gap-2">
						<div className="w-6 h-6 bg-white rounded flex items-center justify-center">
							<span className="font-bold text-sm" style={{ color: '#7A61B2' }}>A</span>
						</div>
						<h2 className="text-lg font-semibold">{readOnly ? 'Visualizar Sinistro' : (sinistro?.id ? 'Editar Sinistro' : 'Novo Sinistro')}</h2>
					</div>
					<button
						onClick={onClose}
						className="text-white hover:text-gray-200 transition-colors"
					>
						<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				{/* Content */}
				<div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
					{/* Checkboxes das flags - Primeira linha */}
					<div className="grid grid-cols-4 gap-4 mb-2">
						<label className="flex items-center gap-2 text-sm text-gray-700">
							<input
								type="checkbox"
								checked={formData.aviso}
								onChange={() => handleCheckboxChange('aviso')}
								disabled={readOnly}
								className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
							/>
							Aviso
						</label>
						<label className="flex items-center gap-2 text-sm text-gray-700">
							<input
								type="checkbox"
								checked={formData.documentos}
								onChange={() => handleCheckboxChange('documentos')}
								disabled={readOnly}
								className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
							/>
							Documentos
						</label>
						<label className="flex items-center gap-2 text-sm text-gray-700">
							<input
								type="checkbox"
								checked={formData.vistoria_sinistro}
								onChange={() => handleCheckboxChange('vistoria_sinistro')}
								disabled={readOnly}
								className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
							/>
							Vistoria do sinistro
						</label>
						<label className="flex items-center gap-2 text-sm text-gray-700">
							<input
								type="checkbox"
								checked={formData.analise}
								onChange={() => handleCheckboxChange('analise')}
								disabled={readOnly}
								className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
							/>
							Análise
						</label>
					</div>

					{/* Checkboxes das flags - Segunda linha */}
					<div className="grid grid-cols-4 gap-4 mb-4">
						<label className="flex items-center gap-2 text-sm text-gray-700">
							<input
								type="checkbox"
								checked={formData.aprovacao}
								onChange={() => handleCheckboxChange('aprovacao')}
								disabled={readOnly}
								className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
							/>
							Aprovação
						</label>
						<label className="flex items-center gap-2 text-sm text-gray-700">
							<input
								type="checkbox"
								checked={formData.oficina}
								onChange={() => handleCheckboxChange('oficina')}
								disabled={readOnly}
								className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
							/>
							Oficina
						</label>
						<label className="flex items-center gap-2 text-sm text-gray-700">
							<input
								type="checkbox"
								checked={formData.concluido}
								onChange={() => handleCheckboxChange('concluido')}
								disabled={readOnly}
								className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
							/>
							Concluído
						</label>
						<label className="flex items-center gap-2 text-sm text-gray-700">
							<input
								type="checkbox"
								checked={formData.pagamento}
								onChange={() => handleCheckboxChange('pagamento')}
								disabled={readOnly}
								className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
							/>
							Pagamento
						</label>
					</div>

					{/* Recusado */}
					<div className="mb-4">
						<label className="flex items-center gap-2 text-sm font-medium text-red-700">
							<input
								type="checkbox"
								checked={formData.recusado}
								onChange={() => handleCheckboxChange('recusado')}
								disabled={readOnly}
								className="w-4 h-4 text-red-600 rounded border-red-300 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
							/>
							Sinistro Recusado
						</label>
						{formData.recusado && (
							<div className="mt-2">
								<label className="block text-sm font-medium text-gray-700 mb-1">Motivo da recusa</label>
								<textarea
									value={formData.motivoRecusa}
									onChange={(e) => handleInputChange('motivoRecusa', e.target.value)}
									readOnly={readOnly}
									disabled={readOnly}
									rows={2}
									className="w-full px-3 py-2 border border-red-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
									placeholder="Descreva o motivo da recusa"
								/>
							</div>
						)}
					</div>
						{/* Primeira linha de campos */}
					<div className="grid grid-cols-3 gap-4 mb-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
							<input
								type="date"
								value={formData.data}
								onChange={(e) => handleInputChange('data', e.target.value)}
								readOnly={readOnly}
								disabled={readOnly}
								className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
							<select
								value={formData.tipo}
								onChange={(e) => handleInputChange('tipo', e.target.value)}
								disabled={readOnly}
								className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
							>
								<option value="">Selecione o tipo de sinistro</option>
								
								<optgroup label="🚗 Seguro Automóvel">
									<option value="Colisão (parcial ou total)">Colisão (parcial ou total)</option>
									<option value="Roubo ou furto">Roubo ou furto</option>
									<option value="Incêndio">Incêndio</option>
									<option value="Danos a terceiros (RC)">Danos a terceiros (RC)</option>
									<option value="Danos por fenômenos naturais (enchente, granizo, raio)">Danos por fenômenos naturais (enchente, granizo, raio)</option>
								</optgroup>
								
								<optgroup label="🏠 Seguro Residencial / Empresarial">
									<option value="Incêndio">Incêndio</option>
									<option value="Explosão">Explosão</option>
									<option value="Danos elétricos">Danos elétricos</option>
									<option value="Vendaval, granizo, alagamento">Vendaval, granizo, alagamento</option>
									<option value="Roubo ou furto qualificado">Roubo ou furto qualificado</option>
									<option value="Responsabilidade civil familiar">Responsabilidade civil familiar</option>
								</optgroup>
								
								<optgroup label="💼 Seguro de Vida / Acidentes Pessoais">
									<option value="Morte natural">Morte natural</option>
									<option value="Morte acidental">Morte acidental</option>
									<option value="Invalidez permanente (total ou parcial)">Invalidez permanente (total ou parcial)</option>
									<option value="Invalidez temporária">Invalidez temporária</option>
									<option value="Despesas médicas e hospitalares por acidente">Despesas médicas e hospitalares por acidente</option>
								</optgroup>
								
								<optgroup label="🚚 Seguro de Transporte">
									<option value="Roubo da carga">Roubo da carga</option>
									<option value="Acidente do veículo transportador">Acidente do veículo transportador</option>
									<option value="Avarias ou extravio da carga">Avarias ou extravio da carga</option>
									<option value="Atraso ou perda total">Atraso ou perda total</option>
								</optgroup>
								
								<optgroup label="💊 Seguro Saúde / Odontológico">
									<option value="Internações">Internações</option>
									<option value="Cirurgias">Cirurgias</option>
									<option value="Consultas médicas">Consultas médicas</option>
									<option value="Tratamentos dentários">Tratamentos dentários</option>
								</optgroup>
								
								<optgroup label="☀️ Seguro Fotovoltaico">
									<option value="Danos elétricos">Danos elétricos</option>
									<option value="Incêndio / Explosão">Incêndio / Explosão</option>
									<option value="Fenômenos naturais (granizo, vendaval, raio, enchente)">Fenômenos naturais (granizo, vendaval, raio, enchente)</option>
									<option value="Roubo / Furto qualificado">Roubo / Furto qualificado</option>
									<option value="Danos por impacto / colisão (queda de objetos, galhos, ferramentas, veículos)">Danos por impacto / colisão (queda de objetos, galhos, ferramentas, veículos)</option>
									<option value="Erro de instalação / montagem">Erro de instalação / montagem</option>
									<option value="Responsabilidade civil (danos a terceiros causados pelo sistema)">Responsabilidade civil (danos a terceiros causados pelo sistema)</option>
									<option value="Perda de produção de energia (interrupção por sinistro coberto)">Perda de produção de energia (interrupção por sinistro coberto)</option>
									<option value="Vício oculto / defeito de fabricação">Vício oculto / defeito de fabricação</option>
									<option value="Danos durante transporte ou armazenamento">Danos durante transporte ou armazenamento</option>
								</optgroup>
							</select>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Valor da franquia</label>
							<input
							type="text"
							value={valorFranquiaDisplay}
							onChange={(e) => handleValorFranquiaChange(e.target.value)}
							readOnly={readOnly}
							disabled={readOnly}
							placeholder="R$ 0,00"
								className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
							/>
						</div>
					</div>

					{/* Segunda linha de campos */}
					<div className="grid grid-cols-3 gap-4 mb-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">n.sinistro</label>
							<input
								type="text"
								value={formData.numero_sinistro}
								onChange={(e) => handleInputChange('numero_sinistro', e.target.value)}
								readOnly={readOnly}
								disabled={readOnly}
								className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Proposta</label>
							<input
								type="text"
								value={formData.proposta}
								onChange={(e) => handleInputChange('proposta', e.target.value)}
								readOnly={readOnly}
								disabled={readOnly}
								className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">CPF/CNPJ</label>
								<input
									type="text"
									value={formatCpfCnpj(formData.cpf_cnpj)}
									onChange={(e) => handleCpfCnpjChange(e.target.value)}
									readOnly={readOnly}
									disabled={readOnly}
									className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
								/>
						</div>
					</div>

					{/* Terceira linha de campos */}
					<div className="grid grid-cols-3 gap-4 mb-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Apólice</label>
							<input
								type="text"
								value={formData.apolice}
								onChange={(e) => handleInputChange('apolice', e.target.value)}
								readOnly={readOnly}
								disabled={readOnly}
								className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Endosso</label>
							<input
								type="text"
								value={formData.endosso}
								onChange={(e) => handleInputChange('endosso', e.target.value)}
								readOnly={readOnly}
								disabled={readOnly}
								className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
							<input
								type="text"
								value={formData.nome}
								onChange={(e) => handleInputChange('nome', e.target.value)}
								readOnly={readOnly}
								disabled={readOnly}
								className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed bg-orange-50"
							/>
						</div>
					</div>

					{/* Campo de informações - largura completa */}
					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">Informações sobre o sinistro</label>
						<textarea
							value={formData.informacoes}
							onChange={(e) => handleInputChange('informacoes', e.target.value)}
							readOnly={readOnly}
							disabled={readOnly}
							rows={4}
							className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
							placeholder="inundação movida rio grande do sul&#10;relação de documentos, movida e grupos estao organizando os documentos e funções de cada um"
						/>
					</div>

					{/* Terceiros */}
					<div className="mb-4 border border-gray-200 rounded-lg p-4">
						<h3 className="text-sm font-semibold text-gray-700 mb-3">Terceiros Envolvidos</h3>
						<div className="grid grid-cols-2 gap-4 mb-3">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Nome do Terceiro</label>
								<input type="text" value={formData.terceiroNome} onChange={(e) => handleInputChange('terceiroNome', e.target.value)} readOnly={readOnly} disabled={readOnly}
									className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed" />
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Contato</label>
								<input type="text" value={formData.terceiroContato} onChange={(e) => handleInputChange('terceiroContato', e.target.value)} readOnly={readOnly} disabled={readOnly}
									className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed" />
							</div>
						</div>
						<div className="grid grid-cols-2 gap-4 mb-3">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Veículo do Terceiro</label>
								<input type="text" value={formData.terceiroVeiculo} onChange={(e) => handleInputChange('terceiroVeiculo', e.target.value)} readOnly={readOnly} disabled={readOnly}
									placeholder="Placa / Modelo" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed" />
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Seguradora do Terceiro</label>
								<input type="text" value={formData.terceiroSeguradora} onChange={(e) => handleInputChange('terceiroSeguradora', e.target.value)} readOnly={readOnly} disabled={readOnly}
									className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed" />
							</div>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Observações sobre Terceiro</label>
							<textarea value={formData.terceiroObs} onChange={(e) => handleInputChange('terceiroObs', e.target.value)} readOnly={readOnly} disabled={readOnly} rows={2}
								className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-gray-50 disabled:cursor-not-allowed" />
						</div>
					</div>
				</div>

				{/* Footer */}
				{!readOnly && (
					<div className="flex justify-between items-center px-6 py-4 border-t bg-gray-50">
						<button
							onClick={handleSave}
							className="px-6 py-2 text-white rounded-md hover:opacity-90 transition-colors font-medium"
							style={{ backgroundColor: '#7A61B2' }}
						>
							Salvar
						</button>
						{sinistro?.id && (
							<button
								type="button"
								onClick={handleEnviarLinkDigisac}
								className="px-4 py-2 text-sm text-white rounded-md hover:opacity-90 transition-colors font-medium flex items-center gap-2"
								style={{ backgroundColor: '#25D366' }}
								title="Enviar link de documentos via Digisac/WhatsApp"
							>
								<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
									<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
								</svg>
								Enviar Link de Documentos
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default SinistroModal;